/**
 * The card-scale chart library: every visual an incident or cadence card can
 * carry, drawn in the cockpit's own language.
 *
 * These are not shrunken dashboards. Each chart answers exactly one question —
 * the question its card's headline asks — and is designed so the answer is
 * legible in the two seconds a reader gives it: direct labels instead of
 * legends, tone only where tone means something, and the current state marked
 * unmistakably.
 *
 * Rules that cost real debugging to learn, now structural:
 *
 * - **Most urgent must look most urgent.** An out-of-stock product draws a
 *   full-width hatched bar and says so. It once drew an empty bar, and the
 *   emergency looked like the healthiest row on the chart.
 * - **One absurd value must not flatten the rest.** Axes cap at a readable
 *   maximum and overflow is written as a number ("180+ days"), not drawn. A
 *   2,550-day overstock once squashed every other bar to a pixel.
 * - **A part-week is hatched and outside the average.** Four days against
 *   seven reads as a collapse, every Tuesday, forever.
 * - **The odd month is drawn as itself** — highlighted, labelled, never
 *   blended into the trend it would invert.
 *
 * Every renderer filters non-finite values before drawing; a NaN in an SVG
 * path fails silently and produces a chart that lies by omission.
 */
import type { ReactElement } from 'react'
import { formatCurrencyCompact } from '../cfoFormat'
import { measureText, truncateToWidth } from '../visuals/textMetrics'
import { CfoChartFrame, useChartTooltip } from './chartInteraction'

const INK = 'var(--cfo-ink, #1c1f26)'
const INK2 = 'var(--cfo-ink-2, #4c5360)'
const MUTED = 'var(--cfo-muted, #868c9a)'
const HAIR = 'var(--cfo-hairline, #d9d3c5)'
const BAR = '#1b4965'
const NEG = 'var(--cfo-neg, #c0492f)'
const WATCH = '#b3801f'
const POS = '#0e7c86'
const INHERIT_FONT = { fontFamily: 'inherit' } as const

const fin = (v: number | null | undefined): v is number =>
  typeof v === 'number' && Number.isFinite(v)

const usd = (v: number): string => formatCurrencyCompact(v)
const pct = (v: number, digits = 0): string => `${(v * 100).toFixed(digits)}%`

/** Two short centred lines beat one rotated line: rotation buys width these
 *  charts do not need, and costs the left-most labels to clipping. Width is
 *  measured in the same SVG units as the rendered text; character counts are
 *  not a safe proxy because wide customer names can cross category bands. */
export function wrapLabel(label: string, maxWidth: number, lines: number, fontSize = 9): string[] {
  const words = label.split(/\s+/).filter(Boolean)
  if (!words.length || lines <= 0 || maxWidth <= 0) return []
  const out: string[] = []
  const options = { size: fontSize, weight: 400 } as const
  let wordIndex = 0

  while (wordIndex < words.length && out.length < lines) {
    const isLastLine = out.length === lines - 1
    if (isLastLine) {
      out.push(truncateToWidth(words.slice(wordIndex).join(' '), maxWidth, options))
      break
    }

    let current = ''
    while (wordIndex < words.length) {
      const next = current ? `${current} ${words[wordIndex]}` : words[wordIndex]
      if (measureText(next, options) > maxWidth) break
      current = next
      wordIndex += 1
    }

    // A single long token still gets a bounded, disclosed label rather than
    // being allowed to spill into its neighbours.
    if (!current) {
      current = truncateToWidth(words[wordIndex], maxWidth, options)
      wordIndex += 1
    }
    out.push(current)
  }
  return out
}

/** The drawable ceiling for cover bars: generous enough to show healthy stock,
 *  capped so one absurd value cannot flatten the rest. */
export function coverAxisMax(days: Array<number | null>, threshold: number): number {
  const finite = days.filter(fin)
  const largest = finite.length ? Math.max(...finite) : threshold
  return Math.min(Math.max(threshold * 2.2, 10), Math.max(largest, threshold * 1.2, 10))
}

export function cumulativeShares(shares: number[]): number[] {
  let running = 0
  return shares.map((s) => (running += s))
}

type Frame = { width: number; height: number; label: string; children: ReactElement[] }

function Svg({ width, height, label, children }: Frame) {
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={label}
      style={{ width: '100%', height: 'auto', display: 'block' }}
    >
      {children}
    </svg>
  )
}

/* ── What each customer owes, by age ─────────────────────────────────── */

export type AgingStackChartRow = {
  label: string
  current: number
  overdue: number
  over_60: number
}

export function AgingStackChart({ rows, caption }: { rows: AgingStackChartRow[]; caption: string }) {
  const { containerRef, tip, show, hide } = useChartTooltip()
  const clean = rows.filter(
    (r) => fin(r.current) && fin(r.overdue) && fin(r.over_60) && r.current + r.overdue + r.over_60 > 0,
  )
  if (!clean.length) return null
  const W = 660
  const rowH = 26
  const top = 22
  const labelW = 150
  const valueW = 78
  const plotW = W - labelW - valueW
  const H = top + clean.length * rowH + 4
  const max = Math.max(...clean.map((r) => r.current + r.overdue + r.over_60))

  const children: ReactElement[] = []
  // The one legend these charts allow: three buckets is past the limit of
  // what direct labels can carry per row.
  const legend: Array<[string, string]> = [
    ['not yet due', '#b9c4cf'],
    ['1–60 days', WATCH],
    ['over 60 days', NEG],
  ]
  let lx = labelW
  legend.forEach(([text, color], i) => {
    children.push(
      <g key={`lg${i}`}>
        <rect x={lx} y={4} width={9} height={9} rx={1.5} fill={color} />
        <text x={lx + 13} y={12} fontSize={10} fill={MUTED} style={INHERIT_FONT}>
          {text}
        </text>
      </g>,
    )
    lx += 13 + text.length * 5.4 + 18
  })

  clean.forEach((row, i) => {
    const y = top + i * rowH
    const scale = (v: number) => (max > 0 ? (v / max) * plotW : 0)
    let x = labelW
    const segments: Array<[number, string]> = [
      [row.current, '#b9c4cf'],
      [row.overdue, WATCH],
      [row.over_60, NEG],
    ]
    const visibleLabel = truncateToWidth(row.label, labelW - 12, { size: 11, weight: 400 })
    children.push(
      <text
        key={`l${i}`}
        x={labelW - 8}
        y={y + 16}
        fontSize={11}
        fill={INK2}
        textAnchor="end"
        style={INHERIT_FONT}
        aria-label={row.label}
        className="cfo-chart-band-hit"
        onMouseEnter={(event) => show(event.clientX, event.clientY, row.label, 'Account')}
        onMouseMove={(event) => show(event.clientX, event.clientY, row.label, 'Account')}
        onMouseLeave={hide}
      >
        <title>{row.label}</title>
        {visibleLabel}
      </text>,
    )
    segments.forEach(([value, color], j) => {
      const w = scale(value)
      if (w > 0.5) {
        children.push(
          <rect key={`s${i}-${j}`} x={x} y={y + 6} width={w} height={13} fill={color} opacity={0.92} />,
        )
      }
      x += w
    })
    children.push(
      <text key={`v${i}`} x={x + 6} y={y + 16} fontSize={10.5} fill={INK2} style={INHERIT_FONT}>
        {usd(row.current + row.overdue + row.over_60)}
      </text>,
    )
  })
  return (
    <CfoChartFrame containerRef={containerRef} tip={tip}>
      <Svg width={W} height={H} label={caption}>{children}</Svg>
    </CfoChartFrame>
  )
}

/* ── Days of supply against a threshold ──────────────────────────────── */

export type CoverBarsChartRow = {
  label: string
  category?: string | null
  days_of_supply?: number | null
  out_of_stock: boolean
  detail: string
}

export function CoverBarsChart({
  rows,
  thresholdDays,
  mode,
  caption,
}: {
  rows: CoverBarsChartRow[]
  thresholdDays: number
  mode: 'stockout' | 'overstock'
  caption: string
}) {
  if (!rows.length) return null
  const W = 660
  const rowH = 38
  const top = 8
  const labelW = 190
  const valueW = 86
  const plotW = W - labelW - valueW
  const H = top + rows.length * rowH + 22
  const axisMax = coverAxisMax(rows.map((r) => r.days_of_supply ?? null), thresholdDays)
  const x = (days: number) => labelW + Math.min(days / axisMax, 1) * plotW
  const thresholdX = x(thresholdDays)

  const children: ReactElement[] = [
    <defs key="defs">
      <pattern id="oos-hatch" patternUnits="userSpaceOnUse" width={6} height={6} patternTransform="rotate(45)">
        <rect width={6} height={6} fill={NEG} opacity={0.16} />
        <line x1={0} y1={0} x2={0} y2={6} stroke={NEG} strokeWidth={2} opacity={0.45} />
      </pattern>
    </defs>,
    // The threshold is the whole question, so it is drawn and named.
    <line
      key="th"
      x1={thresholdX}
      y1={top}
      x2={thresholdX}
      y2={top + rows.length * rowH}
      stroke={INK}
      strokeWidth={1.2}
      strokeDasharray="3 3"
    />,
    <text
      key="thl"
      x={thresholdX}
      y={top + rows.length * rowH + 14}
      fontSize={10}
      fill={INK2}
      textAnchor="middle"
      style={{ font: 'inherit' }}
    >
      {mode === 'stockout' ? `${thresholdDays.toFixed(0)}-day lead time` : `${thresholdDays.toFixed(0)}-day line`}
    </text>,
  ]

  rows.forEach((row, i) => {
    const y = top + i * rowH
    children.push(
      <text key={`l${i}`} x={labelW - 10} y={y + 13} fontSize={11} fill={INK} textAnchor="end" style={{ font: 'inherit' }}>
        {row.label.length > 26 ? `${row.label.slice(0, 25)}…` : row.label}
      </text>,
      <text key={`d${i}`} x={labelW - 10} y={y + 25} fontSize={9} fill={MUTED} textAnchor="end" style={{ font: 'inherit' }}>
        {row.category || 'Uncategorized'}
      </text>,
    )
    if (row.out_of_stock) {
      // Zero cover is the emergency. Full width, hatched, said in words —
      // never an empty bar that reads as the healthiest row on the chart.
      children.push(
        <rect key={`b${i}`} x={labelW} y={y + 4} width={plotW} height={15} fill="url(#oos-hatch)" rx={2} />,
        <rect key={`bb${i}`} x={labelW} y={y + 4} width={Math.max(plotW * 0.04, 8)} height={15} fill={NEG} rx={2} />,
        <text key={`v${i}`} x={labelW + plotW + 8} y={y + 15} fontSize={10.5} fill={NEG} fontWeight={640} style={{ font: 'inherit' }}>
          out of stock
        </text>,
        <text key={`vd${i}`} x={W - 4} y={y + 28} fontSize={8.5} fill={MUTED} textAnchor="end" style={{ font: 'inherit' }}>
          {row.detail}
        </text>,
      )
      return
    }
    const days = row.days_of_supply
    if (!fin(days)) return
    const overflow = days > axisMax
    const barW = Math.max(x(Math.min(days, axisMax)) - labelW, 2)
    const short = mode === 'stockout' ? days < thresholdDays : days > thresholdDays
    children.push(
      <rect
        key={`b${i}`}
        x={labelW}
        y={y + 4}
        width={barW}
        height={15}
        rx={2}
        fill={short ? (mode === 'stockout' ? NEG : WATCH) : BAR}
        opacity={0.88}
      />,
      <text key={`v${i}`} x={labelW + plotW + 8} y={y + 15} fontSize={10.5} fill={INK2} style={{ font: 'inherit' }}>
        {overflow ? `${Math.round(axisMax)}+ days` : `${Math.round(days)} days`}
      </text>,
      <text key={`vd${i}`} x={W - 4} y={y + 28} fontSize={8.5} fill={MUTED} textAnchor="end" style={{ font: 'inherit' }}>
        {row.detail}
      </text>,
    )
  })
  return <Svg width={W} height={H} label={caption}>{children}</Svg>
}

/* ── A value per row against its target ──────────────────────────────── */

export type TargetBulletChartRow = {
  label: string
  value: number
  target?: number | null
  context?: number | null
}

export function TargetBulletsChart({
  rows,
  unit,
  caption,
}: {
  rows: TargetBulletChartRow[]
  unit: 'usd' | 'pct' | 'count' | 'days'
  caption: string
}) {
  const clean = rows.filter((r) => fin(r.value))
  if (!clean.length) return null
  const W = 660
  const rowH = 34
  const labelW = 130
  const valueW = 160
  const plotW = W - labelW - valueW
  const H = clean.length * rowH + 8
  const domainMax =
    Math.max(
      ...clean.map((r) => Math.max(r.value, r.target ?? 0, r.context ?? 0)),
    ) * 1.25 || 1
  const x = (v: number) => labelW + (Math.max(v, 0) / domainMax) * plotW
  const fmt = (v: number) => (unit === 'pct' ? pct(v, 1) : unit === 'usd' ? usd(v) : `${v}`)

  const children: ReactElement[] = []
  clean.forEach((row, i) => {
    const y = i * rowH + 4
    const over = fin(row.target) && row.value > (row.target as number)
    children.push(
      <text key={`l${i}`} x={labelW - 10} y={y + 15} fontSize={11.5} fill={INK} textAnchor="end" style={{ font: 'inherit' }}>
        {row.label.length > 18 ? `${row.label.slice(0, 17)}…` : row.label}
      </text>,
      <rect key={`t${i}`} x={labelW} y={y + 5} width={plotW} height={12} rx={2} fill={HAIR} opacity={0.5} />,
      <rect
        key={`b${i}`}
        x={labelW}
        y={y + 5}
        width={Math.max(x(row.value) - labelW, 2)}
        height={12}
        rx={2}
        fill={over ? NEG : POS}
        opacity={0.9}
      />,
    )
    if (fin(row.target)) {
      children.push(
        <line
          key={`tg${i}`}
          x1={x(row.target)}
          y1={y + 1}
          x2={x(row.target)}
          y2={y + 21}
          stroke={INK}
          strokeWidth={2}
        />,
      )
    }
    if (fin(row.context)) {
      // The trailing figure as a quiet notch: a hot month reads against its
      // own normal, not only against the target.
      children.push(
        <line
          key={`c${i}`}
          x1={x(row.context)}
          y1={y + 3}
          x2={x(row.context)}
          y2={y + 19}
          stroke={INK2}
          strokeWidth={1}
          strokeDasharray="2 2"
        />,
      )
    }
    const deltaText =
      fin(row.target) && unit === 'pct'
        ? ` · ${row.value > (row.target as number) ? '+' : '−'}${Math.abs((row.value - (row.target as number)) * 100).toFixed(1)} pts vs ${pct(row.target as number, 0)} target`
        : fin(row.target)
          ? ` · target ${fmt(row.target as number)}`
          : ' · no target set'
    children.push(
      <text key={`v${i}`} x={W - 4} y={y + 15} fontSize={10.5} fill={over ? NEG : INK2} textAnchor="end" style={{ font: 'inherit' }}>
        {fmt(row.value)}
        {deltaText}
      </text>,
    )
  })
  return <Svg width={W} height={H} label={caption}>{children}</Svg>
}

/* ── Sales by complete week ──────────────────────────────────────────── */

export type WeekBarChartPoint = { label: string; value: number; complete: boolean }

export function WeeksBarsChart({
  weeks,
  median,
  unit = 'usd',
  caption,
}: {
  weeks: WeekBarChartPoint[]
  median?: number | null
  unit?: 'usd' | 'pct' | 'count' | 'days' | 'hours'
  caption: string
}) {
  const clean = weeks.filter((w) => fin(w.value))
  if (clean.length < 2) return null
  const W = 660
  const H = 190
  const top = 18
  const bottom = 30
  const left = 8
  const right = 76
  const plotW = W - left - right
  const plotH = H - top - bottom
  const max = Math.max(...clean.map((w) => w.value), median ?? 0) * 1.12 || 1
  const barW = (plotW / clean.length) * 0.62
  const xAt = (i: number) => left + (i + 0.5) * (plotW / clean.length)
  const yAt = (v: number) => top + plotH - (Math.max(v, 0) / max) * plotH
  const fmt = (v: number) =>
    unit === 'pct' ? pct(v, 1)
    : unit === 'count' ? `${Math.round(v)}`
    : unit === 'hours' ? `${Math.round(v)} h`
    : unit === 'days' ? `${Math.round(v)} d`
    : usd(v)

  const children: ReactElement[] = [
    <defs key="defs">
      <pattern id="wk-hatch" patternUnits="userSpaceOnUse" width={6} height={6} patternTransform="rotate(45)">
        <rect width={6} height={6} fill={BAR} opacity={0.3} />
        <line x1={0} y1={0} x2={0} y2={6} stroke={BAR} strokeWidth={2.4} opacity={0.7} />
      </pattern>
    </defs>,
  ]
  if (fin(median)) {
    const y = yAt(median)
    children.push(
      <line key="md" x1={left} y1={y} x2={left + plotW} y2={y} stroke={INK} strokeWidth={1} strokeDasharray="4 3" opacity={0.65} />,
      <text key="mdl" x={W - 4} y={y + 3.5} fontSize={10} fill={INK2} textAnchor="end" style={{ font: 'inherit' }}>
        usual {fmt(median)}
      </text>,
    )
  }
  clean.forEach((week, i) => {
    const x = xAt(i) - barW / 2
    const y = yAt(week.value)
    children.push(
      <rect
        key={`b${i}`}
        x={x}
        y={y}
        width={barW}
        height={Math.max(top + plotH - y, 1)}
        rx={2}
        fill={week.complete ? BAR : 'url(#wk-hatch)'}
        opacity={week.complete ? 0.88 : 1}
      >
        <title>{`${week.label}: ${fmt(week.value)}${week.complete ? '' : ' so far'}`}</title>
      </rect>,
    )
    if (i % 3 === 0 || i === clean.length - 1) {
      children.push(
        <text key={`x${i}`} x={xAt(i)} y={H - 8} fontSize={9.5} fill={MUTED} textAnchor="middle" style={{ font: 'inherit' }}>
          {week.label}
          {week.complete ? '' : ' · so far'}
        </text>,
      )
    }
  })
  return <Svg width={W} height={H} label={caption}>{children}</Svg>
}

/* ── Gross margin by closed month ────────────────────────────────────── */

export type MonthTrendChartPoint = { period: string; label: string; value: number }

export function MarginTrendChart({
  months,
  usual,
  highlightPeriod,
  caption,
}: {
  months: MonthTrendChartPoint[]
  usual: number
  highlightPeriod?: string | null
  caption: string
}) {
  const clean = months.filter((m) => fin(m.value))
  if (clean.length < 3) return null
  const W = 660
  const H = 180
  const top = 24
  const bottom = 26
  const left = 10
  const right = 84
  const plotW = W - left - right
  const plotH = H - top - bottom
  const values = clean.map((m) => m.value)
  const max = Math.max(...values, usual) + 0.03
  const min = Math.max(Math.min(...values, usual) - 0.03, 0)
  const xAt = (i: number) => left + (clean.length > 1 ? (i / (clean.length - 1)) * plotW : 0)
  const yAt = (v: number) => top + plotH - ((v - min) / (max - min || 1)) * plotH

  const path = clean.map((m, i) => `${i ? 'L' : 'M'}${xAt(i).toFixed(1)},${yAt(m.value).toFixed(1)}`).join(' ')
  const usualY = yAt(usual)

  const children: ReactElement[] = [
    <line key="u" x1={left} y1={usualY} x2={left + plotW} y2={usualY} stroke={INK2} strokeWidth={1} strokeDasharray="4 3" opacity={0.6} />,
    <text key="ul" x={W - 4} y={usualY + 3.5} fontSize={10} fill={INK2} textAnchor="end" style={{ font: 'inherit' }}>
      usual {pct(usual, 1)}
    </text>,
    <path key="p" d={path} fill="none" stroke={BAR} strokeWidth={1.8} strokeLinejoin="round" />,
  ]
  clean.forEach((m, i) => {
    const highlighted = m.period === highlightPeriod
    children.push(
      <circle key={`d${i}`} cx={xAt(i)} cy={yAt(m.value)} r={highlighted ? 4.5 : 2.2} fill={highlighted ? NEG : BAR} />,
    )
    if (highlighted) {
      // The odd month is pointed at, as itself — never blended into a trend
      // it would invert.
      children.push(
        <text key={`hl${i}`} x={xAt(i)} y={yAt(m.value) - 9} fontSize={11} fontWeight={650} fill={NEG} textAnchor="middle" style={{ font: 'inherit' }}>
          {pct(m.value, 1)}
        </text>,
      )
    }
    if (i % 2 === 0 || i === clean.length - 1 || highlighted) {
      children.push(
        <text key={`x${i}`} x={xAt(i)} y={H - 8} fontSize={9.5} fill={MUTED} textAnchor="middle" style={{ font: 'inherit' }}>
          {m.label}
        </text>,
      )
    }
  })
  return <Svg width={W} height={H} label={caption}>{children}</Svg>
}

/* ── Share by customer, cumulative ───────────────────────────────────── */

export function ParetoChart({ rows, caption }: { rows: Array<{ label: string; share: number }>; caption: string }) {
  const { containerRef, tip, show, hide } = useChartTooltip()
  const clean = rows.filter((r) => fin(r.share) && r.share > 0).slice(0, 8)
  if (clean.length < 3) return null
  const W = 660
  const H = 218
  const top = 16
  const bottom = 58
  const left = 8
  const right = 88
  const plotW = W - left - right
  const plotH = H - top - bottom
  const bandW = plotW / clean.length
  const barW = bandW * 0.6
  const labelFontSize = 8.5
  const labelWidth = Math.max(1, bandW - 10)
  const maxShare = Math.max(...clean.map((r) => r.share)) * 1.15
  const cumulative = cumulativeShares(clean.map((r) => r.share))
  const xAt = (i: number) => left + (i + 0.5) * bandW
  const yBar = (v: number) => top + plotH - (v / maxShare) * plotH
  const yCum = (v: number) => top + plotH - Math.min(v, 1) * plotH

  const children: ReactElement[] = []
  clean.forEach((row, i) => {
    children.push(
      <rect key={`b${i}`} x={xAt(i) - barW / 2} y={yBar(row.share)} width={barW} height={top + plotH - yBar(row.share)} rx={2} fill={BAR} opacity={0.85}>
        <title>{`${row.label}: ${pct(row.share)}`}</title>
      </rect>,
    )
    const labelLines = wrapLabel(row.label, labelWidth, 2, labelFontSize)
    children.push(
      <g
        key={`x${i}`}
        aria-label={row.label}
        className="cfo-chart-band-hit"
        onMouseEnter={(event) => show(event.clientX, event.clientY, row.label, 'Customer')}
        onMouseMove={(event) => show(event.clientX, event.clientY, row.label, 'Customer')}
        onMouseLeave={hide}
      >
        <title>{row.label}</title>
        <rect
          x={xAt(i) - labelWidth / 2}
          y={top + plotH + 3}
          width={labelWidth}
          height={bottom - 5}
          fill="transparent"
          pointerEvents="all"
        />
        {labelLines.map((line, li) => (
          <text
            key={li}
            x={xAt(i)}
            y={H - 36 + li * 11}
            fontSize={labelFontSize}
            fill={MUTED}
            textAnchor="middle"
            style={INHERIT_FONT}
          >
            {line}
          </text>
        ))}
      </g>,
    )
  })
  children.push(
    <polyline
      key="cum"
      points={cumulative.map((c, i) => `${xAt(i).toFixed(1)},${yCum(c).toFixed(1)}`).join(' ')}
      fill="none"
      stroke={NEG}
      strokeWidth={1.7}
    />,
  )
  cumulative.forEach((c, i) => {
    children.push(<circle key={`cd${i}`} cx={xAt(i)} cy={yCum(c)} r={2.4} fill={NEG} />)
  })
  const last = cumulative[cumulative.length - 1]
  children.push(
    <text key="cl" x={W - 4} y={yCum(last) + 3.5} fontSize={10.5} fill={NEG} textAnchor="end" style={{ font: 'inherit' }}>
      {pct(last)} together
    </text>,
  )
  return (
    <CfoChartFrame containerRef={containerRef} tip={tip}>
      <Svg width={W} height={H} label={caption}>{children}</Svg>
    </CfoChartFrame>
  )
}

/* ── Months to earn a customer back ──────────────────────────────────── */

export function PaybackChart({
  months,
  comfortMonths,
  cac,
  ltvMultiple,
  caption,
}: {
  months: number
  comfortMonths: number
  cac?: number | null
  ltvMultiple?: number | null
  caption: string
}) {
  if (!fin(months)) return null
  const W = 660
  const H = 96
  const left = 10
  const right = 16
  const plotW = W - left - right
  const domain = Math.max(months, comfortMonths) * 1.25
  const x = (v: number) => left + (v / domain) * plotW
  const barY = 30
  const over = months > comfortMonths

  const children: ReactElement[] = [
    <rect key="t" x={left} y={barY} width={plotW} height={16} rx={3} fill={HAIR} opacity={0.5} />,
    <rect key="b" x={left} y={barY} width={Math.max(x(months) - left, 2)} height={16} rx={3} fill={over ? WATCH : POS} opacity={0.92} />,
    <line key="c" x1={x(comfortMonths)} y1={barY - 8} x2={x(comfortMonths)} y2={barY + 24} stroke={INK} strokeWidth={1.4} strokeDasharray="3 3" />,
    <text key="cl" x={x(comfortMonths)} y={barY - 13} fontSize={10} fill={INK2} textAnchor="middle" style={{ font: 'inherit' }}>
      {comfortMonths.toFixed(0)}-month comfort line
    </text>,
    <text key="v" x={Math.min(x(months) + 8, W - 60)} y={barY + 12} fontSize={11.5} fontWeight={650} fill={over ? WATCH : POS} style={{ font: 'inherit' }}>
      {months.toFixed(1)} months
    </text>,
  ]
  const chips: string[] = []
  if (fin(cac)) chips.push(`${usd(cac)} to acquire`)
  if (fin(ltvMultiple)) chips.push(`${ltvMultiple.toFixed(1)}× lifetime value`)
  if (chips.length) {
    children.push(
      <text key="chips" x={left} y={H - 10} fontSize={10.5} fill={INK2} style={{ font: 'inherit' }}>
        {chips.join(' · ')}
      </text>,
    )
  }
  return <Svg width={W} height={H} label={caption}>{children}</Svg>
}

/* ── Where cash stands ───────────────────────────────────────────────── */

export function CashPositionChart({
  now,
  floor,
  low13w,
  weeksAhead,
  caption,
}: {
  now?: number | null
  floor?: number | null
  low13w?: number | null
  weeksAhead: number[]
  caption: string
}) {
  const marks = [now, floor, low13w].filter(fin)
  const series = weeksAhead.filter(fin)
  if (!marks.length && series.length < 2) return null
  const W = 660
  const H = series.length >= 2 ? 112 : 72
  const left = 18
  const right = 18
  const plotW = W - left - right
  const children: ReactElement[] = []
  if (series.length >= 2) {
    // Cash is a time question, so money belongs on the vertical axis and the
    // 13-week horizon belongs on the horizontal axis. The old renderer put
    // cash on a horizontal number line and then repeated the same facts in a
    // second plot below it. With two governed endpoints that produced a huge,
    // mostly empty panel and put the low label half outside the SVG.
    const values = [...series]
    if (fin(now) && Math.abs(values[0] - now) > Math.max(1, Math.abs(now) * 0.0001)) {
      values.unshift(now)
    }
    const domainValues = [...values, ...(fin(floor) ? [floor] : [])]
    const rawMin = Math.min(...domainValues)
    const rawMax = Math.max(...domainValues)
    const rawSpan = Math.max(rawMax - rawMin, Math.abs(rawMax) * 0.08, 1)
    const min = rawMin - rawSpan * 0.16
    const max = rawMax + rawSpan * 0.18
    const top = 18
    const bottom = 22
    const plotH = H - top - bottom
    const sy = (v: number) => top + ((max - v) / (max - min)) * plotH
    const sx = (i: number) => left + (i / (values.length - 1)) * plotW
    const lowValue = fin(low13w) ? low13w : Math.min(...values)
    const lowIndex = values.reduce((best, value, index) => (
      Math.abs(value - lowValue) < Math.abs(values[best] - lowValue) ? index : best
    ), 0)
    const lowX = sx(lowIndex)
    const lowY = sy(values[lowIndex])
    const lowAnchor = lowIndex === 0 ? 'start' : lowIndex === values.length - 1 ? 'end' : 'middle'
    const breach = lowValue < (fin(floor) ? floor : 0)
    const lowLabel = `13-week low ${usd(lowValue)}`
    const lowLabelW = Math.min(148, 8 + lowLabel.length * 5.8)
    const lowLabelX = lowAnchor === 'start' ? lowX : lowAnchor === 'end' ? lowX - lowLabelW : lowX - lowLabelW / 2
    const lowLabelY = Math.max(1, lowY - 20)

    if (fin(floor)) {
      const floorY = sy(floor)
      children.push(
        <line key="floor" x1={left} y1={floorY} x2={left + plotW} y2={floorY} stroke={WATCH} strokeWidth={1} strokeDasharray="2 3" opacity={0.7} />,
        <text key="floor-label" x={left + plotW} y={Math.max(10, floorY - 4)} fontSize={9.5} fill={WATCH} textAnchor="end" style={INHERIT_FONT}>
          floor {usd(floor)}
        </text>,
      )
    }
    children.push(
      <path
        key="proj"
        d={values.map((v, i) => `${i ? 'L' : 'M'}${sx(i).toFixed(1)},${sy(v).toFixed(1)}`).join(' ')}
        fill="none"
        stroke={BAR}
        strokeWidth={2}
        strokeDasharray="4 4"
        opacity={0.9}
      />,
      <circle key="now" cx={sx(0)} cy={sy(values[0])} r={4.5} fill={INK} />,
      <text key="now-label" x={sx(0)} y={Math.max(11, sy(values[0]) - 8)} fontSize={10.5} fontWeight={650} fill={INK} textAnchor="start" style={INHERIT_FONT}>
        now {usd(fin(now) ? now : values[0])}
      </text>,
      <circle key="low" cx={lowX} cy={lowY} r={4.5} fill={breach ? NEG : WATCH} />,
      <rect key="low-label-bg" x={lowLabelX} y={lowLabelY} width={lowLabelW} height={15} rx={3} fill="var(--cfo-panel, #f6f4ee)" opacity={0.96} />,
      <text key="low-label" x={lowX} y={lowLabelY + 11} fontSize={10} fontWeight={620} fill={breach ? NEG : INK2} textAnchor={lowAnchor} style={INHERIT_FONT}>
        {lowLabel}
      </text>,
      <text key="horizon-start" x={left} y={H - 4} fontSize={9.5} fill={MUTED} textAnchor="start" style={INHERIT_FONT}>
        now
      </text>,
      <text key="horizon-end" x={left + plotW} y={H - 4} fontSize={9.5} fill={MUTED} textAnchor="end" style={INHERIT_FONT}>
        week 13 · projected
      </text>,
    )
  } else {
    // No projection yet: keep the known cash facts in a compact status band.
    const domainValues = [...marks]
    const rawMin = Math.min(...domainValues)
    const rawMax = Math.max(...domainValues)
    const span = Math.max(rawMax - rawMin, Math.abs(rawMax) * 0.08, 1)
    const min = rawMin - span * 0.12
    const max = rawMax + span * 0.12
    const x = (v: number) => left + ((v - min) / (max - min)) * plotW
    const y = 30
    children.push(<line key="axis" x1={left} y1={y} x2={left + plotW} y2={y} stroke={HAIR} strokeWidth={2} />)
    if (fin(now)) {
      children.push(
        <circle key="now" cx={x(now)} cy={y} r={5.5} fill={INK} />,
        <text key="now-label" x={Math.max(left, Math.min(left + plotW, x(now)))} y={14} fontSize={10.5} fontWeight={650} fill={INK} textAnchor={x(now) < W / 2 ? 'start' : 'end'} style={INHERIT_FONT}>
          now {usd(now)}
        </text>,
      )
    }
    if (fin(floor)) {
      children.push(
        <line key="floor" x1={x(floor)} y1={y - 10} x2={x(floor)} y2={y + 10} stroke={WATCH} strokeWidth={2} />,
        <text key="floor-label" x={Math.max(left, Math.min(left + plotW, x(floor)))} y={55} fontSize={9.5} fill={WATCH} textAnchor={x(floor) < W / 2 ? 'start' : 'end'} style={INHERIT_FONT}>
          floor {usd(floor)}
        </text>,
      )
    }
  }
  return <Svg width={W} height={H} label={caption}>{children}</Svg>
}

/* ── Ranked bars with a qualifier ────────────────────────────────────── */

export type RankedBarChartRow = { label: string; value: number; sublabel?: string | null }

export function RankedBarsChart({
  rows,
  unit,
  caption,
}: {
  rows: RankedBarChartRow[]
  unit: 'usd' | 'pct' | 'count' | 'days'
  caption: string
}) {
  const clean = rows.filter((r) => fin(r.value) && r.value > 0)
  if (!clean.length) return null
  const W = 660
  // Match the briefing card's reading rhythm: compact labels at the left,
  // the bar carrying most of the width, and two calm value lines at the right.
  // The prior 170/340/150 split made the names and qualifiers dominate while
  // shrinking the evidence itself; it also packed two text lines too tightly.
  const rowH = 38
  const labelW = 135
  const valueW = 115
  const plotW = W - labelW - valueW
  const H = clean.length * rowH + 6
  const max = Math.max(...clean.map((r) => r.value))
  const fmt = (v: number) => (unit === 'pct' ? pct(v, 1) : unit === 'usd' ? usd(v) : `${v}`)

  const children: ReactElement[] = []
  clean.forEach((row, i) => {
    const y = i * rowH + 3
    const w = Math.max((row.value / max) * plotW, 2)
    children.push(
      <text key={`l${i}`} x={labelW - 10} y={y + 15} fontSize={11} fill={INK} textAnchor="end" style={INHERIT_FONT}>
        {row.label.length > 22 ? `${row.label.slice(0, 21)}…` : row.label}
      </text>,
      <rect key={`b${i}`} x={labelW} y={y + 5} width={w} height={13} rx={2} fill={POS} opacity={0.88} />,
      <text key={`v${i}`} x={labelW + plotW + 8} y={y + 12} fontSize={10.5} fontWeight={640} fill={INK} style={INHERIT_FONT}>
        {fmt(row.value)}
      </text>,
    )
    if (row.sublabel) {
      children.push(
        <text key={`s${i}`} x={labelW + plotW + 8} y={y + 28} fontSize={9} fill={MUTED} style={INHERIT_FONT}>
          {row.sublabel}
        </text>,
      )
    }
  })
  return <Svg width={W} height={H} label={caption}>{children}</Svg>
}
